import * as _ from "lodash";
import { IAction } from "../model/IAction";
import { IElementPanelSettings, IPanelSettings, IProcessPanelSetting, ISinglePanelSetting, ProcessSidePanelType, UIState } from "../model/State";

import * as AddArrayElementActions from "../actions/AddArrayElement";
import * as AddOrUpdateElementActions from "../actions/AddOrUpdateElement";
import * as EditorReadyAction from "../actions/EditorReadyAction";
import * as FetchProcess from "../actions/FetchProcess";
import * as BatchFetchActions from "../actions/InitialFetchActions";
import * as ManualTriggerActions from "../actions/ManualTriggerActions";
import * as ProcessActions from "../actions/NewProcess";
import * as ProcessFromTemplateActions from "../actions/ProcessFromTemplate";
import * as ProcessVariableActions from "../actions/ProcessVariableActions";
import * as RemoveArrayElementActions from "../actions/RemoveArrayElement";
import * as RemoveElementAction from "../actions/RemoveElement";
import * as ReorderArrayElementActions from "../actions/ReorderArrayElement";
import * as SaveProcessActions from "../actions/SaveProcess";
import * as ProcessToLocalStorageActions from "processEditor/actions/ProcessToLocalStorageActions";
import * as SelectElementAction from "../actions/SelectElement";
import * as SidePanelActions from "../actions/SidePanelActions";
import * as SwitchParameterEditorModeActions from "../actions/SwitchParameterEditorMode";
import * as TestProcessActions from "../actions/TestProcessActions";
import * as UpdateBpmnActions from "../actions/UpdateBpmn";
import * as UpdateDescriptionActions from "../actions/UpdateDescription";
import * as UpdateElementDescriptionAction from "../actions/UpdateElementDescription";
import * as UpdateNameActions from "../actions/UpdateName";
import * as UpdateParameterActions from "../actions/UpdateParameter";
import * as SelectConditionalProcessActions from "../actions/SelectConditionalProcess";
import * as UpdatePromoteResultAction from "../actions/UpdatePromoteResultAction";
import * as UpdateSelectedType from "../actions/UpdateSelectedType";
import * as UpdateTargetFrameworkActions from "../actions/UpdateTargetFramework";
import * as UpdateVersionActions from "../actions/UpdateVersion";
import * as UpdateDocumentationPanelActions from "../actions/UpdateDocumentationPanelActions";
import * as UpdateDmn from "../actions/UpdateDmn";
import * as UpdateRetryCount from "../actions/UpdateRetryCount";
import * as UpdateCancellationDisabledActions from "../actions/UpdateCancellationDisabled";
import * as FetchItemsActions from "../actions/FetchDataItems"

import * as AppPath from "../model/AppPath";

import update from "immutability-helper";
import { Utilities } from "shared/utilities";
import { Permissions } from "shared/model/AllPermissions";
import { UPDATE_PROCESS_DOCUMENTATION } from "processEditor/actions/UpdateProcessDocumentation";
import { sidePanelMinSize } from "processEditor/components/ProcessEditor";

const DefaultPanelWidth = 610;

export function uiState(
    state: UIState = {
        editorIsReady: false, disableEdit: false, isLoading: true, isDirty: false, hasChanges: false, parameterEditorModes: {}, isSaving: false, isTestProcessFetching: false,
        panelSettings: {
            elementPanel: { isOpen: false, width: DefaultPanelWidth, selectedElementId: null },
            processSidePanel: { isOpen: true, width: DefaultPanelWidth - 100, selectedPanelType: ProcessSidePanelType.SettingsPanel, isPinned: false },
            documentationPanel: { isOpen: false, width: DefaultPanelWidth }
        },
        showRunOnceDialog: false,
        isIntercomDocumentationEnabled: false,
        tcpTriggerEnabled: false,
        isEditorReadOnly: false,
        isEditingTask: false,
        dmnChangeCount: 0,
    },
    action: IAction): UIState {
    switch (action.type) {
        case AddArrayElementActions.ADD_ARRAY_ELEMENT:
        case RemoveArrayElementActions.REMOVE_ARRAY_ELEMENT:
        case ReorderArrayElementActions.REORDER_ARRAY_ELEMENT:
        case AddOrUpdateElementActions.ADD_GLOBAL_ERROR_HANDLER:
        case AddOrUpdateElementActions.BATCH_UPDATE_ELEMENTS:
        case AddOrUpdateElementActions.CHANGE_OMIT_RESULT_LOGGING:
        case AddOrUpdateElementActions.CHANGE_SHOULD_DISPOSE:
        case UpdateBpmnActions.UPDATE_BPMN:
        case UpdateNameActions.UPDATE_NAME:
        case UpdateVersionActions.UPDATE_MAJOR_VERSION:
        case UpdateVersionActions.UPDATE_MINOR_VERSION:
        case UpdateDescriptionActions.UPDATE_DESCRIPTION:
        case UpdateParameterActions.UPDATE_PARAMETER:
        case UpdateParameterActions.SET_INITIAL_PARAMETER:
        case SelectConditionalProcessActions.SELECT_CONDITIONAL_PROCESS:
        case UpdateTargetFrameworkActions.UPDATE_TARGET_FRAMEWORK:
        case ManualTriggerActions.UPDATE_MANUALTRIGGER_KEY:
        case ManualTriggerActions.ADD_MANUALTRIGGER_KEY:
        case ManualTriggerActions.REMOVE_MANUALTRIGGER_KEY:
        case ManualTriggerActions.UPDATE_MANUALTRIGGER_DESCRIPTION:
        case ManualTriggerActions.UPDATE_MANUALTRIGGER_TYPE:
        case ManualTriggerActions.USE_PROCESS_FOR_MONITORINGRULE_CHANGE:
        case ManualTriggerActions.REORDER_MANUALTRIGGER_PARAMS:
        case UpdatePromoteResultAction.UPDATE_PROMOTE_RESULT_AS:
        case UpdateElementDescriptionAction.UPDATE_ELEMENT_DESCRIPTION:
        case UpdateSelectedType.UPDATE_SELECTED_TYPE:
        case RemoveElementAction.REMOVE_GLOBAL_ERROR_HANDLER:
        case UpdateRetryCount.UPDATE_RETRY_COUNT:
        case UpdateCancellationDisabledActions.UPDATE_CANCELLATION_DISABLED:
        case ProcessVariableActions.ADD_PROCESS_VARIABLE:
        case ProcessVariableActions.UPDATE_PROCESS_VARIABLE_NAME:
        case ProcessVariableActions.UPDATE_PROCESS_VARIABLE_VALUE:
        case ProcessVariableActions.UPDATE_PROCESS_VARIABLE_MODE:
        case ProcessVariableActions.UPDATE_PROCESS_VARIABLE_DESCRIPTION:
        case ProcessVariableActions.TOGGLE_PROCESS_VARIABLE_IS_SECRET:
        case ProcessVariableActions.REMOVE_PROCESS_VARIABLE:
        case ProcessVariableActions.REORDER_PROCESS_VARIABLES:
        case UPDATE_PROCESS_DOCUMENTATION:
            return Object.assign({}, state, { isDirty: state.editorIsReady ? true : false, hasChanges: true } as UIState);

        case ProcessToLocalStorageActions.SAVE_TO_LOCALSTORAGE_OK:
            return Object.assign({}, state, { hasChanges: false } as UIState);

        case ProcessToLocalStorageActions.RESTORED_FROM_LOCALSTORAGE_OK:
            return Object.assign({}, state, { isDirty: true, hasChanges: false } as UIState);

        case FetchItemsActions.INTERCOM_DOCUMENTATION_ENABLED:
            return Object.assign({}, state, { isIntercomDocumentationEnabled: true } as UIState);

        case FetchItemsActions.TCP_TRIGGER_ENABLED:
            return Object.assign({}, state, { tcpTriggerEnabled: true } as UIState);

        case FetchItemsActions.EDITOR_READ_ONLY:
            const editorReadOnlyAction = action as FetchItemsActions.IEditorReadOnlyAction;
            return Object.assign({}, state, { isEditorReadOnly: editorReadOnlyAction.isEditorReadOnly } as UIState);

        case BatchFetchActions.RECEIVE_ALL:
            return Object.assign({}, state, { isLoading: false } as UIState);

        case EditorReadyAction.EDITOR_READY:
            return Object.assign({}, state, { editorIsReady: true } as UIState);

        case FetchProcess.RECEIVE_PROCESS_ERROR:
            return Object.assign({}, state, { isLoading: false } as UIState);

        case FetchProcess.RECEIVE_PROCESS:
            const receiveAction = action as FetchProcess.IReceiveProcessAction;
            const shouldDisableProcessEdit = !Utilities.getUserPermission(Permissions.ProcessEdit);
            return Object.assign({}, state, { disableEdit: receiveAction.processData.draftId != null || receiveAction.processData.isNotLatestVersion || shouldDisableProcessEdit } as UIState);

        case SwitchParameterEditorModeActions.SWITCH_PARAMETER_EDITOR_MODE: {
            const switchModeAction: SwitchParameterEditorModeActions.SwitchParameterEditorModeAction = action as any;
            return update(state, {
                parameterEditorModes: {
                    [switchModeAction.parameterId]: { $set: switchModeAction.newMode }
                }
            });
        }

        case SaveProcessActions.SAVE_PROCESS:
            return Object.assign({}, state, { isSaving: true } as UIState);

        case SaveProcessActions.SAVE_PROCESS_OK:
            return Object.assign({}, state, { isSaving: false, isDirty: false, hasChanges: false } as UIState);

        case SaveProcessActions.BUILD_PROCESS_OK:
        case SaveProcessActions.SAVE_PROCESS_FAILED:
            return Object.assign({}, state, { isSaving: false } as UIState);

        case SaveProcessActions.OPEN_DEPENDENCY_CONSTRAINT_SAVE_MODAL: {
            const openDependencyConstrainSaveModalAction = action as SaveProcessActions.OpenDependencyConstraintModalAction;
            return Object.assign({}, state, { dependencyConstraintsErrorMessage: openDependencyConstrainSaveModalAction.warningMessage, isSaving: false } as UIState);
        }
        case SaveProcessActions.CLOSE_DEPENDENCY_CONSTRAINT_SAVE_MODAL: {
            return Object.assign({}, state, { dependencyConstraintsErrorMessage: null } as UIState);
        }

        case TestProcessActions.START_TEST_PROCESS_FETCH:
            return Object.assign({}, state, { isTestProcessFetching: true } as UIState);

        case TestProcessActions.TEST_PROCESS_COMPLETED:
        case TestProcessActions.FINISH_TEST_PROCESS_FETCH:
            return Object.assign({}, state, { isTestProcessFetching: false } as UIState);

        case SidePanelActions.CLOSE_ELEMENT_PANEL: {
            const newPanelSettings: Partial<IPanelSettings> = {
                elementPanel: { selectedElementId: null, isOpen: false } as IElementPanelSettings,
            };
            return updatePanelState(state, newPanelSettings);
        }

        case SelectElementAction.REMOVE_ELEMENT_SELECTION: {
            const newPanelSettings: Partial<IPanelSettings> = {
                elementPanel: { selectedElementId: null, isOpen: false } as IElementPanelSettings,
                processSidePanel: { isOpen: false } as IProcessPanelSetting, // Lets just always close the panel. If pinned it will still be visible
                documentationPanel: { isOpen: false } as ISinglePanelSetting
            };
            return updatePanelState(state, newPanelSettings);
        }

        case SelectElementAction.SELECT_ELEMENT: {
            const selectAction: SelectElementAction.SelectElementAction = action as any;
            if (selectAction.id) {
                const newPanelSettings = {
                    elementPanel: { selectedElementId: selectAction.id, isOpen: true } as IElementPanelSettings,
                    processSidePanel: { isOpen: false } as IProcessPanelSetting, // Lets just always close the panel. If pinned it will still be visible
                    documentationPanel: { isOpen: false } as ISinglePanelSetting
                };
                return updatePanelState(state, newPanelSettings);
            }
            return state as UIState;
        }

        case SidePanelActions.OPEN_PROCESS_SIDE_PANEL: {
            const openSidePanelAction = action as SidePanelActions.IOpenSidePanelAction;
            return updatePanelState(state,
                {
                    processSidePanel: { selectedPanelType: openSidePanelAction.panelType, isOpen: true } as IProcessPanelSetting
                });
        }

        case SidePanelActions.CLOSE_PROCESS_SIDE_PANEL:
            return updatePanelState(state,
                {
                    processSidePanel: { isPinned: false, isOpen: false } as IProcessPanelSetting
                });

        case SidePanelActions.PIN_PROCESS_SIDE_PANEL:
            return updatePanelState(state,
                {   // Lets set isOpen to true when pinning or unpinning. IsOpen might be false even if panel is visible. We would not like the panel to close when unpinning.
                    processSidePanel: { isPinned: !state.panelSettings.processSidePanel.isPinned, isOpen: true } as IProcessPanelSetting
                });

        case SidePanelActions.TOGGLE_DOCUMENTATION_PANEL: {
            return updatePanelState(state, {
                documentationPanel: {
                    isOpen: !state.panelSettings.documentationPanel.isOpen
                } as ISinglePanelSetting
            });
        }

        case SidePanelActions.CLOSE_DOCUMENTATION_PANEL: {
            return updatePanelState(state, {
                documentationPanel: {
                    isOpen: false
                } as ISinglePanelSetting
            });
        }

        case ProcessFromTemplateActions.NEW_PROCESS_FROM_TEMPLATE: {
            const newFromTemplateAction: ProcessActions.NewProcessAction = action as any;
            return Object.assign({}, state, {
                redirectAfterSaveTo: AppPath.getApiManagementLink(newFromTemplateAction.processData.apiSpecId)
            });
        }

        case SaveProcessActions.OPEN_RUN_ONCE_DIALOG: {
            return Object.assign({}, state, { showRunOnceDialog: true } as UIState);
        }

        case SaveProcessActions.CLOSE_RUN_ONCE_DIALOG: {
            return Object.assign({}, state, { showRunOnceDialog: false } as UIState);
        }

        case UpdateDocumentationPanelActions.UPDATE_DOCUMENTATION_PANEL: {
            const updateDocumentationAction: UpdateDocumentationPanelActions.UpdateDocumentationPanelAction = action as any;
            return Object.assign({}, state, { documentation: updateDocumentationAction.documentation } as UIState);
        }

        case SidePanelActions.TOGGLE_EDIT_TASK: {
            const toggleEditTaskAction: SidePanelActions.IToggleEditTaskAction = action as any;
            return Object.assign({}, state, { isEditingTask: toggleEditTaskAction.show } as UIState);
        }

        case SidePanelActions.UPDATE_PANEL_WIDTH: {
            const updatePanelWidthAction: SidePanelActions.IUpdatePanelWidthAction = action as any;

            if (updatePanelWidthAction.width < sidePanelMinSize) {
                return updatePanelState(state,
                    {
                        [updatePanelWidthAction.panelType]: { width: sidePanelMinSize } as ISinglePanelSetting,
                    });
            }

            return updatePanelState(state,
                {
                    [updatePanelWidthAction.panelType]: { width: updatePanelWidthAction.width } as ISinglePanelSetting,
                });
        }

        case UpdateDmn.UPDATE_DMN:
            return Object.assign({}, state, { dmnChangeCount: state.dmnChangeCount + 1 } as UIState);

        default:
            return state;
    }
}

function updatePanelState(state: UIState, newPanelSettings: Partial<IPanelSettings>): UIState {
    const panelSettings: IPanelSettings = Object.assign({}, state.panelSettings);
    const updatedPanelSettings: IPanelSettings = _.merge(panelSettings, newPanelSettings);
    return Object.assign({}, state, { panelSettings: updatedPanelSettings });
}
